import { formatDateTime } from '@/lib/date-time';
/**
 * @file routes/_app/settings/staff.tsx
 * @description 員工帳號管理頁面（Task 65）。
 *
 * 功能：
 * - 列表查詢（username / name / email / 角色 / 啟用狀態 / 最後登入）
 * - 新增員工（含初始密碼）
 * - 編輯員工（登入帳號/姓名/Email/電話/角色/啟用狀態；序 52 系統管理員可改登入帳號）
 * - 軟刪除（停用帳號）
 * - 重設密碼（操作欄額外按鈕；序51 支援「恢復初始密碼」1234567890）
 *
 * 視覺：ListPageShell + SectionCard + StatusPill + FilterBar
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
} from 'antd';
import { message, modal } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import {
  KeyRound,
  Plus,
  Search,
  Edit3,
  Users,
  UserCheck,
  RefreshCw,
  FileText,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_INITIAL_PASSWORD,
  PASSWORD_RULE_HINT,
  USER_ROLE_LABELS,
  userCan,
} from '@zhcb/shared';
import { adminPasswordRule } from '@/lib/password-rules';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { useRoleList } from '@/services/roles';
import { ListPageShell, PageMetaItem, FilterBar, StatusPill } from '@/components/shell';
import { color, font, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/staff')({ component: StaffPage });

/**
 * 員工帳號實體類型（對應後端 users 表的精簡視圖）。
 * 動態 RBAC（Task 12）：roles / primaryRole 為 string，支援自定義角色代碼。
 */
interface Staff {
  id: number;
  username: string;
  name: string;
  email: string;
  phone: string | null;
  /** 主角色（選單／工作台分發用） */
  primaryRole: string;
  /** 角色能力清單（多角色） */
  roles: string[];
  isActive: boolean;
  lastLoginAt: string | null;
}

/** 後端分頁回應結構 */
interface StaffListResponse {
  items: Staff[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  /** 全量統計：啟用中人數（伺服器分頁下不能用當頁 items 計算） */
  activeCount: number;
  /** 全量統計：具稽核員角色人數 */
  auditorCount: number;
}

/** 角色 → tone 映射（用於表格 Pill 視覺強調） */
const roleToneMap: Record<string, 'danger' | 'brand' | 'info' | 'warning' | 'neutral'> = {
  admin: 'danger',
  manager: 'warning',
  decider: 'info',
  auditor: 'brand',
  // 會計（序52）以 neutral 中性色呈現，與其餘角色區隔
  accountant: 'neutral',
};

/**
 * 角色代碼 → 中文顯示名稱。
 *
 * 優先使用靜態 USER_ROLE_LABELS 快速回落（內置角色），
 * 若代碼不在靜態表中則由呼叫端以 DB name 補充（自定義角色）。
 * 抓不到 DB name 時直接顯示代碼。
 *
 * @param code      角色代碼
 * @param dbNameMap DB 角色 code→name 映射（由 useRoleList 取得）
 */
function resolveRoleLabel(code: string, dbNameMap: Map<string, string>): string {
  return (
    (USER_ROLE_LABELS as Record<string, string | undefined>)[code] ?? dbNameMap.get(code) ?? code
  );
}

/** 員工帳號管理頁面 */
function StaffPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const actor = useAuthStore((state) => state.user);
  // 序156：刪除登錄帳號權限——沿用頁面既有 user.manage 能力口徑（後端 DELETE 端點同一能力）
  const canDelete = !!actor && userCan(actor, 'user.manage');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterRole, setFilterRole] = useState<string | undefined>();
  // 伺服器端分頁狀態：page / pageSize 需傳給後端，否則永遠只拿到預設前 20 筆
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();

  // 動態角色列表（Task 12）：從 DB 讀取 active 角色，供角色過濾下拉與表格角色欄使用
  const { data: rolesData } = useRoleList();
  /** active 角色清單（過濾掉 disabled） */
  const activeRoles = (rolesData?.items ?? []).filter((r) => r.status === 'active');
  /** DB 角色 code→name 映射（用於自定義角色的中文名稱回落） */
  const dbRoleNameMap = new Map(activeRoles.map((r) => [r.code, r.name]));

  /** 查詢員工列表（伺服器端分頁） */
  const { data, isLoading } = useQuery({
    queryKey: ['settings-staff', searchText, filterRole, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchText) params.set('search', searchText);
      if (filterRole) params.set('role', filterRole);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      return apiClient.get<StaffListResponse>(`/api/settings/staff?${params.toString()}`);
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  // 啟用中 / 稽核員為後端全量聚合統計，不能用當頁 items 計算（當頁最多 pageSize 筆）
  const activeCount = data?.activeCount ?? 0;
  const auditorCount = data?.auditorCount ?? 0;

  /** 開啟新增表單 */
  const handleAdd = () => {
    setEditingStaff(null);
    form.resetFields();
    setModalOpen(true);
  };

  /** 開啟編輯表單 */
  const handleEdit = (record: Staff) => {
    setEditingStaff(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  /** 刪除（軟刪除停用）員工帳號 Mutation（序156：系統管理員可刪除登錄帳號） */
  const deleteStaff = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/settings/staff/${id}`),
    onSuccess: () => {
      message.success('帳號已刪除');
      void qc.invalidateQueries({ queryKey: ['settings-staff'] });
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '刪除失敗，請稍後再試');
    },
  });

  /** 二次確認後刪除帳號；禁止刪除自己登入中的帳號 */
  const handleDelete = (record: Staff) => {
    if (actor && record.id === actor.id) {
      message.warning('無法刪除自己目前登入的帳號');
      return;
    }
    modal.confirm({
      title: '刪除登錄帳號',
      content: `確定要刪除帳號「${record.username}（${record.name}）」嗎？刪除後該帳號將無法登入。`,
      okText: '刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => deleteStaff.mutateAsync(record.id),
    });
  };

  /** 表格欄位定義 */
  const columns: ColumnsType<Staff> = [
    {
      title: '帳號',
      dataIndex: 'username',
      width: 200,
      // 超長帳號（如自動生成的測試帳號）以省略號截斷，避免溢出蓋到相鄰列
      ellipsis: { showTitle: true },
      render: (v: string) => (
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.sm,
            color: color.text.secondary,
            fontWeight: weight.medium,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: '姓名',
      dataIndex: 'name',
      width: 140,
      ellipsis: { showTitle: true },
      render: (v: string) => (
        <span style={{ fontWeight: weight.medium, color: color.text.primary }}>{v}</span>
      ),
    },
    {
      title: '信箱',
      dataIndex: 'email',
      width: 240,
      ellipsis: { showTitle: true },
      render: (v: string) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
          {v}
        </span>
      ),
    },
    { title: '電話', dataIndex: 'phone', width: 120 },
    {
      title: '角色',
      dataIndex: 'roles',
      width: 200,
      // 多角色：主角色（primaryRole）標「主」，其餘角色並列展示
      // 動態 RBAC（Task 12）：自定義角色代碼先查 USER_ROLE_LABELS，再查 DB name，抓不到顯示代碼
      render: (_: unknown, record: Staff) => {
        const roles = record.roles?.length ? record.roles : [record.primaryRole];
        return (
          <Space size={4} wrap>
            {roles.map((r) => (
              <StatusPill key={r} tone={roleToneMap[r] ?? 'neutral'} size="sm">
                {resolveRoleLabel(r, dbRoleNameMap) + (r === record.primaryRole ? '（主）' : '')}
              </StatusPill>
            ))}
          </Space>
        );
      },
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      width: 80,
      render: (v: boolean) => (
        <StatusPill tone={v ? 'success' : 'muted'} size="sm" dot>
          {v ? '啟用' : '停用'}
        </StatusPill>
      ),
    },
    {
      title: '最後登入',
      dataIndex: 'lastLoginAt',
      width: 160,
      render: (v: string | null) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.tertiary }}>
          {v ? formatDateTime(v) : '—'}
        </span>
      ),
    },
    {
      title: '操作',
      key: '_actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          {/* 跳轉至員工檔案詳情頁（Task 7） */}
          <Tooltip title="員工檔案">
            <Button
              type="text"
              size="small"
              icon={<FileText size={14} />}
              onClick={() =>
                void navigate({
                  to: '/settings/staff/$id',
                  params: { id: String(record.id) },
                })
              }
            />
          </Tooltip>
          <Tooltip title="編輯">
            <Button
              type="text"
              size="small"
              icon={<Edit3 size={14} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <ResetPasswordIconButton staffId={record.id} />
          {canDelete && (
            <Tooltip title="刪除帳號">
              <Button
                type="text"
                size="small"
                danger
                icon={<Trash2 size={14} />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <ListPageShell
        title="員工帳號管理"
        description="管理所有系統員工的帳號資料、角色與登入狀態。"
        meta={
          <>
            <PageMetaItem icon={<Users size={13} />} label="員工總數" value={`${total} 人`} />
            <PageMetaItem
              icon={<UserCheck size={13} />}
              label="啟用中"
              value={`${activeCount} 人`}
            />
            <PageMetaItem label="稽核員" value={`${auditorCount} 人`} />
          </>
        }
        actions={
          <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
            新增員工
          </Button>
        }
        filters={
          <FilterBar
            search={
              <Input
                placeholder="搜尋帳號 / 姓名 / 信箱"
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  // 搜尋條件變更後回到第 1 頁，避免停留在超出結果範圍的頁碼
                  setPage(1);
                }}
                allowClear
              />
            }
            filters={
              /* 動態 RBAC（Task 12）：角色下拉改為從 DB active 角色讀取 */
              <Select
                placeholder="角色"
                value={filterRole}
                onChange={(v) => {
                  setFilterRole(v);
                  // 角色過濾變更後回到第 1 頁
                  setPage(1);
                }}
                allowClear
                options={activeRoles.map((r) => ({
                  value: r.code,
                  label: resolveRoleLabel(r.code, dbRoleNameMap),
                }))}
                style={{ width: 140 }}
              />
            }
            extra={
              (searchText || filterRole) && (
                <Button
                  type="text"
                  size="small"
                  icon={<RefreshCw size={13} />}
                  onClick={() => {
                    setSearchText('');
                    setFilterRole(undefined);
                    setPage(1);
                  }}
                >
                  重置
                </Button>
              )
            }
          />
        }
      >
        <Table<Staff>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          scroll={{ x: 1210 }}
          pagination={{
            // 伺服器端分頁：current / pageSize / total 受控，翻頁時重新請求後端
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 筆資料`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </ListPageShell>

      {/* 新增/編輯 Modal */}
      <Modal
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <UserCheck size={16} color={color.brand.primary} />
            {editingStaff ? '編輯員工' : '新增員工'}
          </span>
        }
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={960}
        destroyOnHidden
      >
        <StaffForm
          item={editingStaff}
          onClose={() => {
            setModalOpen(false);
            form.resetFields();
            void qc.invalidateQueries({ queryKey: ['settings-staff'] });
          }}
          form={form}
        />
      </Modal>
    </>
  );
}

/**
 * 員工帳號新增/編輯表單。
 * - 新增模式：顯示帳號 + 初始密碼欄位
 * - 編輯模式：顯示啟用狀態開關，隱藏帳號欄位
 *
 * 動態 RBAC（Task 12）：角色多選與主角色下拉改為從 DB active 角色讀取。
 */
function StaffForm({
  item,
  onClose,
  form,
}: {
  item: Staff | null;
  onClose: () => void;
  form: ReturnType<typeof Form.useForm>[0];
}) {
  const qc = useQueryClient();

  // 動態角色列表（Task 12）：從 DB 讀取 active 角色，複用 useRoleList（TanStack Query 會走快取）
  const { data: rolesData } = useRoleList();
  const activeRoles = (rolesData?.items ?? []).filter((r) => r.status === 'active');
  const dbRoleNameMap = new Map(activeRoles.map((r) => [r.code, r.name]));
  /** 角色 Select 選項（label 優先靜態中文名，其次 DB name，再顯示代碼） */
  const roleOptions = activeRoles.map((r) => ({
    value: r.code,
    label: resolveRoleLabel(r.code, dbRoleNameMap),
  }));

  // 監聽已選角色，用於約束「主角色」下拉的可選項（主角色必須是已選角色之一）
  const selectedRoles = (Form.useWatch('roles', form) ?? []) as string[];

  /** 新增員工 Mutation */
  const create = useMutation({
    mutationFn: (v: unknown) => apiClient.post('/api/settings/staff', v),
    onSuccess: () => {
      message.success('員工帳號已建立');
      void qc.invalidateQueries({ queryKey: ['settings-staff'] });
      onClose();
    },
  });

  /** 更新員工 Mutation */
  const update = useMutation({
    mutationFn: (v: unknown) => apiClient.patch(`/api/settings/staff/${item!.id}`, v),
    onSuccess: () => {
      message.success('員工資料已更新');
      void qc.invalidateQueries({ queryKey: ['settings-staff'] });
      onClose();
    },
    // 帳號衝突（409 DUPLICATE_USERNAME）等錯誤直接把後端訊息回顯給操作者
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '更新失敗，請稍後再試');
    },
  });

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={item ?? { roles: ['auditor'], primaryRole: 'auditor', isActive: true }}
      onFinish={(v) => {
        // 主角色若未落在已選角色內，前端兜底補正（後端亦會 normalize）
        const raw = v as { roles?: string[]; primaryRole?: string };
        const roles = raw.roles ?? [];
        const primaryRole = roles.includes(raw.primaryRole ?? '') ? raw.primaryRole : roles[0];
        const payload = { ...raw, primaryRole };
        return item ? update.mutate(payload) : create.mutate(payload);
      }}
      style={{ marginTop: 16 }}
    >
      {/* 新增模式：帳號 + 初始密碼各佔半欄 */}
      {!item && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="username"
              label="登入帳號"
              rules={[{ required: true, min: 3, max: 50 }]}
            >
              <Input placeholder="至少 3 個字元" />
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* 序51：預設帶入系統初始密碼 1234567890；可改為自訂（6–10 字元英數混合） */}
            <Form.Item
              name="initialPassword"
              label="初始密碼"
              initialValue={DEFAULT_INITIAL_PASSWORD}
              extra={`預設 ${DEFAULT_INITIAL_PASSWORD}；自訂須 ${PASSWORD_RULE_HINT}`}
              rules={[{ required: true, message: '請輸入初始密碼' }, adminPasswordRule]}
            >
              <Input.Password maxLength={10} autoComplete="new-password" />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* 姓名 + 信箱 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="name" label="姓名" rules={[{ required: true, max: 100 }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="email" label="信箱" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* 角色能力（多選）：使用者可同時擔任多個角色，如「稽核員＋驗證決定委員」 */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="roles"
            label="角色能力（可多選）"
            rules={[{ required: true, message: '請至少選擇一個角色' }]}
            tooltip="使用者可同時擁有多個角色，權限取所有角色的並集。例如稽核員兼任驗證決定委員，可對「非本人稽核」的案件投票。"
          >
            {/* 動態 RBAC（Task 12）：選項改為從 DB active 角色讀取，含自定義角色 */}
            <Select mode="multiple" placeholder="請選擇一個或多個角色" options={roleOptions} />
          </Form.Item>
        </Col>
      </Row>

      {/* 電話 + 主角色（選單／工作台分發用，須為已選角色之一） */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="phone" label="聯絡電話">
            <Input maxLength={20} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="primaryRole"
            label="主角色"
            rules={[{ required: true, message: '請選擇主角色' }]}
            tooltip="決定登入後進入哪個工作台與選單，須為上方已選角色之一。"
          >
            {/* 主角色下拉：限縮為已選角色；若尚未選角色則顯示所有 active 角色 */}
            {/* 動態 RBAC（Task 12）：同樣使用 DB active 角色 */}
            <Select
              placeholder="請選擇主角色"
              options={
                selectedRoles.length
                  ? roleOptions.filter((o) => selectedRoles.includes(o.value))
                  : roleOptions
              }
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 編輯模式：登入帳號（序 52 系統管理員可修改）+ 帳號啟用 */}
      {item && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="username"
              label="登入帳號"
              tooltip="修改後該員工需以新帳號登入；系統會檢查是否重複。"
              rules={[
                { required: true, message: '登入帳號不可為空' },
                { min: 3, max: 50, message: '登入帳號長度需為 3-50 個字元' },
                {
                  pattern: /^[A-Za-z0-9._-]+$/,
                  message: '僅能包含英文、數字與 . _ - 符號',
                },
              ]}
            >
              <Input placeholder="至少 3 個字元" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="isActive" label="帳號啟用" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Form.Item style={{ marginBottom: 0 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={create.isPending || update.isPending}>
            {item ? '儲存' : '建立'}
          </Button>
          {/* 編輯模式額外提供重設密碼功能 */}
          {item && <ResetPasswordButton staffId={item.id} />}
        </Space>
      </Form.Item>
    </Form>
  );
}

/** 重設密碼表單值：mode=initial 恢復初始密碼；mode=custom 自訂新密碼 */
interface ResetPasswordFormValues {
  mode: 'initial' | 'custom';
  newPassword?: string;
}

/**
 * 重設密碼對話框 hook（序51，2026-09-25）。
 *
 * 提供兩種方式：
 * - 「恢復初始密碼」：一鍵恢復為系統預設初始密碼 1234567890（送 `restoreInitial: true`）；
 * - 「自訂新密碼」：6–10 字元、英數混合、不限大小寫（規則與後端共用 @zhcb/shared）。
 *
 * @param staffId 目標員工 ID
 * @returns 開啟對話框的函式
 */
function useResetPasswordDialog(staffId: number) {
  const [pwForm] = Form.useForm<ResetPasswordFormValues>();

  const reset = useMutation({
    mutationFn: (v: { newPassword?: string; restoreInitial?: boolean }) =>
      apiClient.post(`/api/settings/staff/${staffId}/reset-password`, v),
    onSuccess: (_data, v) =>
      message.success(
        v.restoreInitial ? `已恢復初始密碼（${DEFAULT_INITIAL_PASSWORD}）` : '密碼已重設',
      ),
    onError: (e: Error) => {
      console.error('[settings/staff] reset password failed', e);
      message.error(e.message || '重設密碼失敗');
    },
  });

  return function openResetPasswordDialog() {
    pwForm.resetFields();
    modal.confirm({
      title: '重設密碼',
      content: (
        <Form<ResetPasswordFormValues>
          form={pwForm}
          layout="vertical"
          className="mt-2"
          initialValues={{ mode: 'initial' }}
        >
          <Form.Item name="mode" style={{ marginBottom: 12 }}>
            <Radio.Group>
              <Space orientation="vertical">
                <Radio value="initial">恢復初始密碼（{DEFAULT_INITIAL_PASSWORD}）</Radio>
                <Radio value="custom">自訂新密碼</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {/* 選「自訂新密碼」時才顯示輸入框 */}
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.mode !== next.mode}>
            {({ getFieldValue }) =>
              getFieldValue('mode') === 'custom' ? (
                <Form.Item
                  name="newPassword"
                  label="新密碼"
                  extra={PASSWORD_RULE_HINT}
                  rules={[{ required: true, message: '請輸入新密碼' }, adminPasswordRule]}
                >
                  <Input.Password maxLength={10} autoComplete="new-password" />
                </Form.Item>
              ) : (
                <div style={{ fontSize: 12, color: color.text.tertiary }}>
                  員工以初始密碼登入後，系統會提示其盡速修改密碼。
                </div>
              )
            }
          </Form.Item>
        </Form>
      ),
      okText: '確認重設',
      onOk: async () => {
        const v = await pwForm.validateFields();
        await reset.mutateAsync(
          v.mode === 'initial' ? { restoreInitial: true } : { newPassword: v.newPassword },
        );
        pwForm.resetFields();
      },
    });
  };
}

/**
 * 重設密碼圖示按鈕（行內操作欄用）。
 */
function ResetPasswordIconButton({ staffId }: { staffId: number }) {
  const openDialog = useResetPasswordDialog(staffId);
  return (
    <Tooltip title="重設密碼">
      <Button type="text" size="small" icon={<KeyRound size={14} />} onClick={openDialog} />
    </Tooltip>
  );
}

/**
 * 重設密碼按鈕（表單底部用）。
 */
function ResetPasswordButton({ staffId }: { staffId: number }) {
  const openDialog = useResetPasswordDialog(staffId);
  return (
    <Button icon={<KeyRound size={14} />} onClick={openDialog}>
      重設密碼
    </Button>
  );
}
